import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "@/lib/api";
import { dateFmt } from "@/lib/format";
import { PageLoader, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Megaphone } from "lucide-react";

export default function Marketing() {
  const { data: posts, isLoading } = useQuery({ queryKey: ["author-posts"], queryFn: async () => (await api.get("/author/posts")).data });
  const [active, setActive] = useState(null);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Marketing</p>
        <p className="text-sm text-muted-foreground mt-1">Tips and updates from Manda Publishers to help you sell more books.</p>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : posts.length === 0 ? (
        <EmptyState icon={Megaphone} title="No posts yet" desc="Marketing tips and updates will appear here." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Card key={p.id} data-testid={`author-post-${p.id}`} onClick={() => setActive(p)}
              className="overflow-hidden flex flex-col cursor-pointer hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
              {p.cover_url ? (
                <img src={p.cover_url} alt={p.title} className="h-40 w-full object-cover" />
              ) : (
                <div className="h-40 w-full bg-secondary flex items-center justify-center">
                  <Megaphone className="h-8 w-8 text-primary/40" />
                </div>
              )}
              <div className="p-5 flex-1 flex flex-col">
                <span className="text-xs text-muted-foreground">{dateFmt(p.created_at)}</span>
                <p className="font-head font-semibold mt-1 leading-tight line-clamp-2">{p.title}</p>
                <p className="text-sm text-muted-foreground mt-1.5 line-clamp-3 flex-1">{p.excerpt}</p>
                <span className="text-sm text-primary font-medium mt-3">Read more →</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">{active?.title}</DialogTitle>
          </DialogHeader>
          {active?.cover_url && <img src={active.cover_url} alt={active.title} className="w-full max-h-64 object-cover rounded-lg" />}
          <p className="text-xs text-muted-foreground">{dateFmt(active?.created_at)}</p>
          <div className="markdown-body text-sm leading-relaxed text-foreground/90">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{active?.content || ""}</ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
